import { Component, OnDestroy, WritableSignal, afterNextRender, computed, effect, input, output, signal, viewChildren } from "@angular/core"
import { MatIcon } from "@angular/material/icon";
import { EmployeeMbrad } from "src/app/models";
import { MatButton } from '@angular/material/button';
import { CdkFixedSizeVirtualScroll, CdkVirtualScrollViewport, CdkVirtualForOf } from "@angular/cdk/scrolling";
import { MatFormField } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import {
  CdkDragDrop,
  CdkDrag,
  CdkDropList,
} from "@angular/cdk/drag-drop";
import { MatCard, MatCardContent } from "@angular/material/card";

const FILTER_TIME = 500;
const ITEM_SIZE = 64;
const ALWAYS_ITEM_SIZE = 56;

@Component({
    selector: "valben-employees-pick-list",
    templateUrl: "employees-pick-list.component.html",
    standalone: true,
    imports: [
        CdkFixedSizeVirtualScroll,
        CdkVirtualScrollViewport,
        CdkVirtualForOf,
        CdkDrag,
        CdkDropList,
        MatCard,
        MatCardContent,
        MatInput,
        MatFormField,
        MatIcon,
        MatButton,
    ],
    styles: [`
        .cdk-drag-animating {
            transition: transform 250ms cubic-bezier(0, 0, 0.2, 1);
        }

        .cdk-drop-list-dragging .cdk-drag:not(.cdk-drag-placeholder) {
            transition: transform 250ms cubic-bezier(0, 0, 0.2, 1);
        }

        .cdk-drag-placeholder {
            opacity: 0;
        }

        .cdk-drag-preview {
            box-sizing: border-box;
            border-radius: 4px;
            box-shadow: 0 5px 5px -3px rgba(0, 0, 0, 0.2),
                        0 8px 10px 1px rgba(0, 0, 0, 0.14),
                        0 3px 14px 2px rgba(0, 0, 0, 0.12);
        }
    `],
})
export class EmployeesPickListComponent implements OnDestroy {
    readonly itemSize = ITEM_SIZE;
    readonly alwaysItemSize = ALWAYS_ITEM_SIZE;

    public values = input.required<{
        in: EmployeeMbrad[],
        out: EmployeeMbrad[],
        exIn: EmployeeMbrad[],
        exOut: EmployeeMbrad[],
    }>();
    public selected = output<{ in: EmployeeMbrad[], ex: EmployeeMbrad[] }>();

    protected delaySearchIn = signal<string>("");
    protected delaySearchOut = signal<string>("");
    protected delaySearchAin = signal<string>("");
    protected delaySearchAout = signal<string>("");

    protected inList = signal<EmployeeMbrad[]>([]);
    protected outList = signal<EmployeeMbrad[]>([]);
    protected alwaysInList = signal<EmployeeMbrad[]>([]);
    protected alwaysOutList = signal<EmployeeMbrad[]>([]);

    protected filterInList = computed(() => {
        const i = this.inList();
        const s = this.delaySearchIn().toLowerCase();
        return s ? i.filter(e => `${e.fullName} ${e.mbrad}`.toLowerCase().includes(s)) : i;
    });
    protected filterOutList = computed(() => {
        const o = this.outList();
        const s = this.delaySearchOut().toLowerCase();
        return s ? o.filter(e => `${e.fullName} ${e.mbrad}`.toLowerCase().includes(s)) : o;
    });
    protected filterAlwaysIncluded = computed(() => {
        const ni = this.alwaysInList();
        const s = this.delaySearchAin().toLowerCase();
        return s ? ni.filter(e => `${e.fullName} ${e.mbrad}`.toLowerCase().includes(s)) : ni;
    });
    protected filterAlwaysExcluded = computed(() => {
        const no = this.alwaysOutList();
        const s = this.delaySearchAout().toLowerCase();
        return s ? no.filter(e => `${e.fullName} ${e.mbrad}`.toLowerCase().includes(s)) : no;
    });

    private readonly _viewports = viewChildren(CdkVirtualScrollViewport);
    private _filterTimer: ReturnType<typeof setTimeout> | undefined;

    constructor() {
        afterNextRender(() => this.refreshViewports());

        effect(() => {
            this.restoreValues();
            this.refreshViewports();
        }, { allowSignalWrites: true });
    }

    ngOnDestroy(): void {
        clearTimeout(this._filterTimer);
    }

    refreshViewports(): void {
        requestAnimationFrame(() => {
            this._viewports().forEach(vp => vp.checkViewportSize());
        });
    }

    protected search(value: string, sig: WritableSignal<string>): void {
        if (this._filterTimer) clearTimeout(this._filterTimer);

        this._filterTimer = setTimeout(() => {
            sig.set(value.trim().toLowerCase());
        }, FILTER_TIME);
    }

    protected drop(event: CdkDragDrop<EmployeeMbrad[], EmployeeMbrad[], EmployeeMbrad>): void {
        if (event.previousContainer === event.container) return;
        const item = event.item.data;

        const isInList = event.previousContainer.id === "inList";
        const sourceSig = isInList ? this.inList : this.outList;
        const destSig = isInList ? this.outList : this.inList;

        sourceSig.set(sourceSig().filter(x => x.masterId !== item.masterId));
        destSig.set([item, ...destSig().filter(x => x.masterId !== item.masterId)]);

        const includedByFilters = this.values().in.some(x => x.masterId === item.masterId);
        this.alwaysInList.set(this.alwaysInList().filter(x => x.masterId !== item.masterId));
        this.alwaysOutList.set(this.alwaysOutList().filter(x => x.masterId !== item.masterId));

        if (isInList === includedByFilters) {
            const addList = isInList ? this.alwaysOutList : this.alwaysInList;
            addList.set([...addList(), item]);
        }

        this.selected.emit({ in: this.alwaysInList(), ex: this.alwaysOutList(), });

        this.refreshViewports();
    }

    protected reset(): void {
        this.restoreValues();
        this.selected.emit({ in: this.alwaysInList(), ex: this.alwaysOutList() });
        this.refreshViewports();
    }

    private restoreValues(): void {
        const v = this.values();
        const unique = (employees: EmployeeMbrad[]) =>
            [...new Map(employees.map(e => [e.masterId, e])).values()];
        const excluded = unique(v.exOut);
        const excludedIds = new Set(excluded.map(e => e.masterId));
        const included = unique(v.exIn).filter(e => !excludedIds.has(e.masterId));
        const includedIds = new Set(included.map(e => e.masterId));

        this.inList.set(unique([...v.in, ...included]).filter(e => !excludedIds.has(e.masterId)));
        this.outList.set(unique([...v.out, ...excluded]).filter(e => !includedIds.has(e.masterId)));
        this.alwaysInList.set(included);
        this.alwaysOutList.set(excluded);
    }
}
